// Compact shift write approval and receipt state.
import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { type CANCELLED_BY } from '@apps/web/features/phoebe_chat_v3/model/shift_write_card';
import { type PermissionStatus } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call';
import {
    type ApprovalEditor,
    type PermissionOutcome,
    type WriteResultPresentation,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import { CompactWriteCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_write_card';
import {
    type EhrOverrideAnswers,
    EhrOverrideField,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/ehr_override_field';
import {
    EhrRatePickerField,
    type EhrRatePicks,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/ehr_rate_picker_field';
import {
    CARD_CONTROL_CLASS,
    EhrTagChips,
    useEhrNoteOptions,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/ehr_tag_picker_field';
import { cn } from '@apps/web/lib/utils';

type CancelledBy = (typeof CANCELLED_BY)[number];

const CANCELLED_BY_LABELS: Record<CancelledBy, string> = {
    office: 'Cancelled by office',
    caregiver: 'Cancelled by caregiver',
    client: 'Cancelled by client',
};

export function ShiftWriteApprovalView({
    editor,
    organizationId,
    outcome,
    receipt,
    status,
    submitting,
    onAllow,
    onDeny,
}: Readonly<{
    editor: Extract<ApprovalEditor, { kind: 'shift_write' }>;
    organizationId: string;
    outcome: PermissionOutcome;
    receipt?: WriteResultPresentation;
    status: PermissionStatus;
    submitting: boolean;
    onAllow: (argumentsOverride?: Record<string, unknown>) => void;
    onDeny: () => void;
}>): ReactNode {
    const picker = editor.rate_picker;
    const [picks, setPicks] = useState<EhrRatePicks | null>(null);
    const [tagIds, setTagIds] = useState<ReadonlyArray<string>>(
        editor.tag_picker?.tag_ids ?? [],
    );
    const [note, setNote] = useState(editor.cancel_note?.note ?? '');
    const [cancelledBy, setCancelledBy] = useState<CancelledBy>(
        editor.cancel_note?.cancelled_by ?? 'office',
    );
    const onPicked = useCallback(
        (next: EhrRatePicks | null) => setPicks(next),
        [],
    );
    const overrideEditor = editor.override_editor;
    const [allowed, setAllowed] = useState<EhrOverrideAnswers>({});
    const onAllowedChange = useCallback(
        (next: EhrOverrideAnswers) => setAllowed(next),
        [],
    );
    // A warning the coordinator has not allowed still blocks the vendor, so
    // Approve waits until every one this write hit is answered.
    const overridesAnswered =
        overrideEditor === undefined ||
        overrideEditor.warnings.every(
            (warning) => allowed[warning.key] === true,
        );
    // An empty object is not "no edit": the backend reads any non-null
    // override as a settings edit and refuses the approval.
    const cancelNote = editor.cancel_note;
    const submittedOverride = useMemo(() => {
        if (cancelNote !== undefined) {
            return {
                ehr_cancel: {
                    tag_ids: tagIds,
                    note,
                    cancelled_by: cancelledBy,
                },
            };
        }
        const override = {
            ...(picks === null ? {} : { ehr_rate: picks }),
            ...(overrideEditor === undefined ? {} : { ehr_overrides: allowed }),
        };
        return Object.keys(override).length === 0 ? undefined : override;
    }, [cancelNote, tagIds, note, cancelledBy, picks, overrideEditor, allowed]);
    return (
        <CompactWriteCard
            card={
                outcome === 'succeeded'
                    ? (receipt?.shiftCard ?? editor.card)
                    : editor.card
            }
            state={
                status === 'waiting'
                    ? {
                          status: 'pending',
                          disabled: submitting,
                          allowDisabled:
                              (picker !== undefined && picks === null) ||
                              !overridesAnswered ||
                              (editor.cancel_note !== undefined &&
                                  note.trim() === ''),
                          onAllow: () => onAllow(submittedOverride),
                          onDeny,
                      }
                    : {
                          status:
                              status === 'denied'
                                  ? 'denied'
                                  : outcome === 'pending'
                                    ? 'running'
                                    : outcome === 'succeeded' &&
                                        receipt?.shiftCard === undefined
                                      ? 'failed'
                                      : outcome,
                          resultSummary:
                              outcome === 'succeeded' &&
                              receipt?.shiftCard === undefined
                                  ? receipt?.summary
                                  : undefined,
                      }
            }
            notices={
                outcome === 'succeeded' ? receipt?.notices : editor.notices
            }
        >
            {overrideEditor !== undefined && (
                <EhrOverrideField
                    editor={overrideEditor}
                    disabled={submitting}
                    onChange={onAllowedChange}
                />
            )}
            {picker !== undefined && (
                <EhrRatePickerField
                    picker={picker}
                    organizationId={organizationId}
                    disabled={submitting}
                    onChange={onPicked}
                />
            )}
            {editor.cancel_note !== undefined && status === 'waiting' && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                    <Select
                        value={cancelledBy}
                        disabled={submitting}
                        onValueChange={(value) =>
                            setCancelledBy(value as CancelledBy)
                        }
                    >
                        <SelectTrigger
                            aria-label="Cancelled by"
                            className={cn(
                                CARD_CONTROL_CLASS,
                                'w-auto gap-1 py-0 data-[size=default]:h-7 [&_svg]:size-3',
                            )}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {editor.cancel_note.cancelled_by_options.map(
                                (option) => (
                                    <SelectItem key={option} value={option}>
                                        {CANCELLED_BY_LABELS[option]}
                                    </SelectItem>
                                ),
                            )}
                        </SelectContent>
                    </Select>
                    <Input
                        aria-label="Cancellation note"
                        value={note}
                        disabled={submitting}
                        onChange={(event) => setNote(event.target.value)}
                        className={cn(CARD_CONTROL_CLASS, 'min-w-40 flex-1')}
                    />
                </div>
            )}
            {editor.tag_picker !== undefined && status === 'waiting' && (
                <CancelTagPicker
                    organizationId={organizationId}
                    shiftId={editor.tag_picker.shift_id}
                    value={tagIds}
                    disabled={submitting}
                    onChange={setTagIds}
                />
            )}
        </CompactWriteCard>
    );
}

function CancelTagPicker({
    organizationId,
    shiftId,
    value,
    disabled,
    onChange,
}: Readonly<{
    organizationId: string;
    shiftId: string;
    value: ReadonlyArray<string>;
    disabled: boolean;
    onChange: (tagIds: ReadonlyArray<string>) => void;
}>): ReactNode {
    const options = useEhrNoteOptions(organizationId, { shift_id: shiftId });
    return (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            <EhrTagChips
                options={options}
                value={value}
                disabled={disabled}
                onChange={onChange}
            />
        </div>
    );
}
